import { ROOM_KEYS } from "../Components/PassengersData/PassengersData";
import {
  isVpManualAddedRoom,
  resolveVpRoomLevelDisplayPrice,
} from "./vpManualAddRoom";

export const VP_ROOM_CARD_INCLUDED_IN_TOTAL_HE = "כלול בסה״כ ההזמנה";
export const VP_ROOM_CARD_ALLOCATED_NOTE_HE = "מחושב יחסית לסה״כ התשלום";

const TOL_ILS = 4;
const LS_TRACE = "DEBUG_VP_ROOM_CARD_PRICE_SSOT";

/** @type {Record<string, string>} */
const _lastTraceFingerprintByKey = Object.create(null);

function _isDev() {
  try {
    return import.meta.env?.DEV === true;
  } catch {
    return false;
  }
}

function _localVerbose() {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(LS_TRACE) === "1";
  } catch {
    return false;
  }
}

function _round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

function _num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Same basis as getPackageProfilePriceDisplay sidebar total. */
export function resolveVpSidebarPayableIls(ourPrice, billableAttractionsIls = 0) {
  const extra = Number(billableAttractionsIls) || 0;
  const cc = _num(ourPrice?.priceWithCreditCardFee);
  if (cc != null && cc > 0) return _round2(cc + extra);
  const pwcc = _num(ourPrice?.priceWithCommissionAndCreditCardFee);
  if (pwcc != null && pwcc > 0) return _round2(pwcc + extra);
  return null;
}

export function readVpRoomCardRawPwccIls(room) {
  return _num(room?.ourFareDetails?.ourTotalFare?.priceWithCommissionAndCreditCardFee);
}

/**
 * Sum PWCC from currently selected rooms in profile slots (extended matrix per slot).
 * @param {object[]|null|undefined} roomsAndPaxInfo
 */
export function sumVpSelectedRoomsRawPwccIls(roomsAndPaxInfo) {
  const slots = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  let sum = 0;
  let count = 0;
  for (const slot of slots) {
    const raw = readVpRoomCardRawPwccIls(slot?.room);
    if (raw != null && raw > 0) {
      sum += raw;
      count += 1;
    }
  }
  return { roomsSummedIls: _round2(sum), selectedCount: count };
}

function _roomSeq(room) {
  if (!room || typeof room !== "object") return null;
  return (
    room["@seqNum"] ??
    room.roomId ??
    room.RoomId ??
    room[ROOM_KEYS.SEQ_NUM] ??
    null
  );
}

function _isSelectedRoomOption(room, roomsAndPaxInfo, roomIndex) {
  const slots = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  const slot = slots[roomIndex];
  const selectedSeq = _roomSeq(slot?.room);
  const optionSeq = _roomSeq(room);
  if (selectedSeq == null || optionSeq == null) return false;
  return String(selectedSeq) === String(optionSeq);
}

function _withUsd(displayPriceIls, ilsToUsd) {
  return {
    displayPriceIls,
    displayPriceUsd:
      displayPriceIls != null && typeof ilsToUsd === "function"
        ? _num(ilsToUsd(displayPriceIls))
        : null,
  };
}

/**
 * @param {{
 *   room?: object|null,
 *   roomIndex?: number,
 *   roomsAndPaxInfo?: object[]|null,
 *   ourPrice?: object|null,
 *   ldsSupplement?: object|null,
 *   handleFee?: number|null,
 *   selectedResult?: object|null,
 *   billableAttractionsIls?: number,
 *   ilsToUsd?: (n: number) => number,
 * }} input
 */
export function resolveVpRoomCardDisplayPrice(input = {}) {
  const {
    room = null,
    roomIndex = 0,
    roomsAndPaxInfo = [],
    ourPrice = null,
    billableAttractionsIls = 0,
    handleFeeData = null,
    ilsToUsd,
  } = input;

  const slots = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  const slot = slots[roomIndex] ?? null;
  const roomCount = slots.length;

  const isManualAddedRoom = isVpManualAddedRoom(room, slot);
  const effectiveRoom =
    isManualAddedRoom && slot?.room
      ? {
          ...(room && typeof room === "object" ? room : {}),
          ...slot.room,
          ourFareDetails:
            slot.room.ourFareDetails ??
            room?.ourFareDetails ??
            null,
        }
      : room;

  const roomCardRawPrice = readVpRoomCardRawPwccIls(effectiveRoom);
  const sidebarTotalIls = resolveVpSidebarPayableIls(ourPrice, billableAttractionsIls);
  const ourPricePwcc = _num(ourPrice?.priceWithCommissionAndCreditCardFee);
  const { roomsSummedIls, selectedCount } = sumVpSelectedRoomsRawPwccIls(roomsAndPaxInfo);

  const roomLevel = resolveVpRoomLevelDisplayPrice(
    effectiveRoom,
    slot,
    handleFeeData
  );
  const hasRoomFareDetails = Boolean(
    effectiveRoom?.ourFareDetails?.ourTotalFare &&
      typeof effectiveRoom.ourFareDetails.ourTotalFare === "object"
  );
  const roomFarePwcc = roomCardRawPrice;
  const selectedAddRoomPrice = _num(
    effectiveRoom?.selectedAddRoomPriceIls ?? slot?.selectedAddRoomPriceIls
  );
  const isSelectedOption = _isSelectedRoomOption(room, roomsAndPaxInfo, roomIndex);

  const driftDetected =
    sidebarTotalIls != null &&
    selectedCount > 0 &&
    Math.abs(roomsSummedIls - sidebarTotalIls) > TOL_ILS;

  /** @type {{ displayPriceIls: number|null, displayPriceUsd: number|null, source: string, isAllocated: boolean, explanation: string|null, action: string }} */
  let outcome;

  // Single room slot: only the *selected* option aligns with sidebar SSOT.
  // Alternate room-type options must use their own matrix ourFareDetails price.
  if (roomCount <= 1 && isSelectedOption && sidebarTotalIls != null) {
    outcome = {
      ..._withUsd(sidebarTotalIls, ilsToUsd),
      source: "ourPrice.priceWithCreditCardFee_sidebar_ssot",
      isAllocated: false,
      explanation: null,
      action: "single_room_sidebar_ssot",
    };
  } else if (isManualAddedRoom && roomLevel.valid && roomLevel.priceIls != null) {
    outcome = {
      ..._withUsd(roomLevel.priceIls, ilsToUsd),
      source:
        roomLevel.via === "manual_add_room_with_room_markup"
          ? "manual_add_room_with_room_markup"
          : roomLevel.via === "selectedAddRoomPriceIls"
            ? "manual_add_room_selectedAddRoomPriceIls"
            : "manual_add_room_ourFareDetails",
      isAllocated: false,
      explanation: null,
      action: "manual_add_room_room_level_price",
    };
  } else if (roomLevel.valid && roomLevel.priceIls != null && isSelectedOption) {
    outcome = {
      ..._withUsd(roomLevel.priceIls, ilsToUsd),
      source: "room_ourFareDetails",
      isAllocated: false,
      explanation: null,
      action: "selected_room_level_price",
    };
  } else if (!driftDetected && roomCardRawPrice != null) {
    outcome = {
      ..._withUsd(roomCardRawPrice, ilsToUsd),
      source:
        "room.ourFareDetails.ourTotalFare.priceWithCommissionAndCreditCardFee",
      isAllocated: false,
      explanation: null,
      action: "multi_room_matrix_matches_sidebar",
    };
  } else if (driftDetected && !roomLevel.valid) {
    outcome = {
      displayPriceIls: null,
      displayPriceUsd: null,
      source: "included_in_booking_total",
      isAllocated: false,
      explanation: VP_ROOM_CARD_INCLUDED_IN_TOTAL_HE,
      action: isSelectedOption
        ? "drift_no_room_price_included_label"
        : "drift_unselected_included_label",
    };
  } else if (roomCardRawPrice != null) {
    outcome = {
      ..._withUsd(roomCardRawPrice, ilsToUsd),
      source:
        "room.ourFareDetails.ourTotalFare.priceWithCommissionAndCreditCardFee",
      isAllocated: false,
      explanation: null,
      action: "browse_option_matrix_price",
    };
  } else {
    outcome = {
      displayPriceIls: null,
      displayPriceUsd: null,
      source: "none",
      isAllocated: false,
      explanation: null,
      action: "fallback_empty",
    };
  }

  return {
    ...outcome,
    roomIndex,
    roomCount,
    roomCardRawPrice,
    roomsSummedIls,
    sidebarTotalIls,
    ourPricePwcc,
    driftDetected,
    isManualAddedRoom,
    hasRoomFareDetails,
    roomFarePwcc,
    selectedAddRoomPrice,
    isSelectedOption,
  };
}

/**
 * @param {ReturnType<typeof resolveVpRoomCardDisplayPrice> & { dealId?: string|null }} payload
 * @param {{ force?: boolean }} [opts]
 */
export function logVpRoomCardPriceSsotTrace(payload, opts = {}) {
  if (!_isDev() || !payload) return;

  const dealKey = payload.dealId != null ? String(payload.dealId) : "(no-deal)";
  const rowKey = `${dealKey}:${payload.roomIndex ?? "?"}`;
  const fingerprint = JSON.stringify({
    selectedDisplayPrice: payload.displayPriceIls,
    source: payload.source,
    isAllocated: payload.isAllocated,
    driftDetected: payload.driftDetected,
    action: payload.action,
    isManualAddedRoom: payload.isManualAddedRoom,
  });

  if (!opts.force && !_localVerbose() && _lastTraceFingerprintByKey[rowKey] === fingerprint) {
    return;
  }
  _lastTraceFingerprintByKey[rowKey] = fingerprint;

  // eslint-disable-next-line no-console
  console.info("[VP_ROOM_CARD_PRICE_SSOT_TRACE]", {
    roomIndex: payload.roomIndex,
    roomCount: payload.roomCount,
    isManualAddedRoom: payload.isManualAddedRoom,
    hasRoomFareDetails: payload.hasRoomFareDetails,
    roomFarePwcc: payload.roomFarePwcc,
    selectedAddRoomPrice: payload.selectedAddRoomPrice,
    roomCardRawPrice: payload.roomCardRawPrice,
    roomsSummedIls: payload.roomsSummedIls,
    sidebarTotalIls: payload.sidebarTotalIls,
    ourPricePwcc: payload.ourPricePwcc,
    selectedDisplayPrice: payload.displayPriceIls,
    source: payload.source,
    isAllocated: payload.isAllocated,
    driftDetected: payload.driftDetected,
    action: payload.action,
    explanation: payload.explanation ?? null,
  });
}
